import { ISalePageDetailV2Response } from '@/api/types';
import { Column, Divider, Element, Row } from '@vuddy/components';
import { formatInputDate2 } from '@vuddy/utils';
import { ReactNode } from 'react';

interface Props {
  salePage?: Partial<ISalePageDetailV2Response>;
}

interface InstructionItem {
  label: string;
  value: ReactNode;
}

const formatCurrency = (amount: number) => {
  return amount.toLocaleString() + '원';
};

export const SalePageInstructions = ({ salePage }: Props) => {
  if (!salePage) return null;

  const hasExchangeTickets =
    salePage.products?.some(
      product =>
        Array.isArray(product.exchangeTickets) &&
        product.exchangeTickets.length > 0
    ) ?? false;

  const instructions: InstructionItem[] = [];
  const isReservation = salePage.physical?.method === 'RESERVATION';
  const isFunding = salePage.type === 'FUNDING';

  if (salePage.funding?.targetAmount) {
    instructions.push({
      label: '목표 금액',
      value: (
        <Element typography="bodyXsAccent">
          {formatCurrency(salePage.funding.targetAmount)}
        </Element>
      ),
    });
  }

  if (
    (isReservation || isFunding) &&
    salePage.saleTimeRange?.startAt &&
    salePage.saleTimeRange?.endAt
  ) {
    const startDate = formatInputDate2(salePage.saleTimeRange.startAt);
    const endDate = formatInputDate2(salePage.saleTimeRange.endAt);
    instructions.push({
      label: isFunding ? '펀딩 기간' : '예약 기간',
      value: (
        <Element typography="bodyXsAccent">{`${startDate} ~ ${endDate}`}</Element>
      ),
    });
  }
  // 배송비
  if (salePage.physical?.deliveryFee || salePage.funding?.deliveryFee) {
    const deliveryFee =
      salePage.physical?.deliveryFee || salePage.funding?.deliveryFee;
    const defaultFee = deliveryFee?.defaultFee
      ? formatCurrency(deliveryFee.defaultFee.amount)
      : '무료';
    const remoteAreaFee = deliveryFee?.remoteAreaFee
      ? formatCurrency(deliveryFee.remoteAreaFee.amount)
      : null;

    const freeShippingThreshold = salePage.saleBundle?.freeShippingThreshold;

    instructions.push({
      label: '배송비',
      value: (
        <Column gap="space_2xs">
          <Element typography="bodyXsAccent">{defaultFee}</Element>
          <Element typography="captionBase" color="tertiary">
            제주, 도서지역 {remoteAreaFee}
          </Element>
          {freeShippingThreshold && (
            <Element typography="captionBaseAccent" color="secondary">
              {formatCurrency(freeShippingThreshold.amount)} 이상 구매 시 배송비
              무료
            </Element>
          )}
        </Column>
      ),
    });
  }

  if (
    salePage.physical?.shippingStartDate ||
    salePage.funding?.shippingStartDate
  ) {
    const shippingDate =
      salePage.physical?.shippingStartDate ||
      salePage.funding?.shippingStartDate;
    const formattedDate = shippingDate ? formatInputDate2(shippingDate) : '';
    instructions.push({
      label: '배송 예상일',
      value: (
        <Column>
          <Element typography="bodyXsAccent" color="vuddyPrimary">
            {`${formattedDate} 이후 순차발송`}
          </Element>
          <Element typography="captionBase" color="tertiary">
            배송 예상일 기준으로 순차 발송되며, 물류 상황에 따라 소폭 일정이
            조정될 수 있습니다.
          </Element>
        </Column>
      ),
    });
  }

  if (salePage.type === 'DIGITAL' || hasExchangeTickets) {
    instructions.push({
      label: '디지털 굿즈',
      value: (
        <Column>
          <Element typography="bodyXsAccent" color="vuddyPrimary">
            {'마이박스에서 확인'}
          </Element>
          <Element typography="captionBase" color="tertiary">
            디지털 굿즈는 구매 확정 후 일괄 지급됩니다
          </Element>
        </Column>
      ),
    });
  }

  if (instructions.length === 0) return null;

  const renderTableRow = ({
    label,
    value,
    isLast = false,
  }: {
    label: string;
    value: ReactNode;
    isLast?: boolean;
  }) => (
    <Column key={label} gap="gap_xl">
      <Row alignItems="center" gap="gap_base">
        <Element
          typography="captionBaseAccent"
          color="tertiary"
          style={{ width: '72px' }}>
          {label}
        </Element>
        <Column flex={1}>{value}</Column>
      </Row>
      {!isLast && <Divider />}
    </Column>
  );

  return (
    <Column gap="gap_large">
      {instructions.map((instruction, index) =>
        renderTableRow({
          label: instruction.label,
          value: instruction.value,
          isLast: index === instructions.length - 1,
        })
      )}
    </Column>
  );
};
